/**
 * The "Related Solutions, Capabilities and Products" section of an industry page: a title and
 * introduction, then one card per group (in today's copy, a solutions group and a "Products"
 * group), each listing links with an icon or image and ending in a "jump to" link. It renders
 * `./Mobile/RelatedSolutionsCapabilitiesAndProducts.tsx` below `lg` and
 * `./Desktop/RelatedSolutionsCapabilitiesAndProducts.tsx` from `lg` up; both draw each link with
 * `./Shared/RelatedSolutionsCapabilitiesAndProductsImageComponent.tsx`.
 *
 * Position: an `AllSections` entry on every Ryerson industry page under
 * `src/pages/metal-solutions/industries/` and on the dormant STS pages under
 * `src/pages/metal-solutions/industries-served/`. Each passes one language of its content
 * module's object: `RelatedSolutionsCapabilitiesContent` on most Ryerson pages,
 * `RelatedSolutionsAndCapabilitiesContent` on transportation, and names such as
 * `TechnologyRelatedSolutionsCapabilitiesContent` on STS. All are typed with
 * {@link RelatedSolutionsCapabilitiesAndProductsProperties}.
 *
 * Links are site paths (for example `/metal-solutions/capabilities/fabrication`,
 * `/steel/steel-plate`). Every "Products" card's `jumpToLink` is `/store`, which
 * `src/pages/_app.tsx` → `App` hands to the store app with a full page load.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import RelatedSolutionsCapabilitiesAndProductsDesktop from "./Desktop/RelatedSolutionsCapabilitiesAndProducts";
import RelatedSolutionsCapabilitiesAndProductsMobile from "./Mobile/RelatedSolutionsCapabilitiesAndProducts";
import { IconList } from "@ryerson/frontend.assets";

/** One link in a group. */
export type Bullet = {
	title: string;
	/** Image shown instead of an icon when set. */
	imageUrl?: string;
	/** Site path the link goes to. */
	link: string;
	/** `@ryerson/frontend.assets` icon shown when there is no `imageUrl`. Default `"gear"`. */
	iconType?: keyof IconList & string;
};

/** One card: a group title, its links, and a closing "jump to" link. */
export type SecondaryContent = {
	title: string;
	/** Label of the closing link, for example "More Metal Solutions". */
	jumpToLabel: string;
	/** Site path of the closing link, for example `/metal-solutions/` or `/store`. */
	jumpToLink: string;
	bullets: Bullet[];
};

/** Copy for one language of the section; content modules type their objects with it. */
export interface RelatedSolutionsCapabilitiesAndProductsProperties {
	/** Desktop heading. Pages also use it as the `AllSections` title (and anchor). */
	title: string;
	/** Introductory paragraph. */
	mainDescription: string;
	/** The cards, in display order. The desktop styles space a title column and two cards. */
	secondaryContent: SecondaryContent[];
}

/** Renders the section for the current breakpoint; see the file header. */
const RelatedSolutionsCapabilitiesAndProducts: React.FC<
	RelatedSolutionsCapabilitiesAndProductsProperties
> = (props) => {
	return (
		<>
			<Media lessThan="lg">
				<RelatedSolutionsCapabilitiesAndProductsMobile {...props} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<RelatedSolutionsCapabilitiesAndProductsDesktop {...props} />
			</Media>
		</>
	);
};
export default RelatedSolutionsCapabilitiesAndProducts;
